import * as React from 'react'
import {
  Drawer, Box, Typography, IconButton, Button, TextField, MenuItem, Chip, Tooltip, Alert,
  RadioGroup, Radio, FormControlLabel, Checkbox, InputAdornment,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import AcUnitOutlinedIcon from '@mui/icons-material/AcUnitOutlined'
import VideocamOutlinedIcon from '@mui/icons-material/VideocamOutlined'
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined'

import {primaryBtnSx, secondaryBtnSx} from '../../../styles/patterns'
import {FONT, SIZE, WEIGHT} from '../../../styles/foundation/typography'
import {NAVY, SUBTLE, BORDER, PRIMARY_BLUE, WARNING, SUCCESS, ERROR, withOpacity, MUTED} from '../../../styles/foundation/colors'

import {fmtKg, fmtCurrency} from './format'
import {singleMerchantOf} from './data/scope'
import {
  PACKS, packOf, packOptionLabel,
} from './data/packs'
import {subcatOf, UNCATEGORISED} from './data/subcats'
import {volumetricKg, VOLUMETRIC_DIVISOR} from './state/volumetric'

import {chipSx, sentenceChipSx} from './freeze/tone'
import {
  SHOT_SLOTS, IMAGE_TYPES, VIDEO_TYPES, IMAGE_FORMATS, VIDEO_FORMATS,
  MAX_IMAGE_BYTES, MAX_VIDEO_BYTES, mb, DECLARATION, CLOSING_NOTE, GOVERNS_SENTENCE,
} from './freeze/copy'
import {
  int, skuKey, dimsText, shipWord,
  weightFacts, chargeableKg, slabBreakdown, deriveProposal,
  LEVEL, LEVELS, levelDisabled, levelDisabledReason, subcatOptions, subcatReach,
  partitionRows, validateImage, validateVideo,
  buildEvidenceFiles, hasAttachedEvidence, evidenceScope,
  gapsFor, gapSentence,
} from './freeze/request'
import CartonSVG from './freeze/CartonSVG'
import KeyValue from './freeze/KeyValue'
import DropZone from './freeze/DropZone'


const labelSx = {fontFamily: FONT, fontSize: SIZE['2xs'], color: SUBTLE}

// SIZE.xs, the section-heading size every other drawer in both weight modules
// already uses. Weight and colour are what separate a heading from the body
// under it here, not a second size step.
const sectionLabelSx = {fontFamily: FONT, fontWeight: WEIGHT.bold, fontSize: SIZE.xs, color: NAVY, mb: 0.75}

const alertSx = {fontFamily: FONT, fontSize: SIZE['2xs'], py: 0.75, '& .MuiAlert-message': {lineHeight: 1.55}}

const subtleTextSx = {fontFamily: FONT, fontSize: SIZE['2xs'], color: SUBTLE}

/**
 * Every field in this drawer carries a hint saying where its number comes from
 * or what it commits the merchant to, so the helper line is styled once.
 *
 * MUI leaks 16px into .MuiInputBase-input from theme.typography.body1, which
 * beats inheritance, and draws the required asterisk in the label's own grey —
 * where it reads as punctuation rather than a marker. Both are beaten here
 * (rules 14 and D11).
 */
const fieldSx = {
  fontFamily: FONT,
  fontSize: SIZE.xs,
  '& .MuiInputBase-input': {fontFamily: FONT, fontSize: SIZE.xs},
  '& .MuiInputLabel-root': {fontFamily: FONT},
  '& .MuiFormHelperText-root': {fontFamily: FONT, fontSize: SIZE['2xs'], color: SUBTLE, ml: 0.25, mr: 0},
  '& .MuiFormLabel-asterisk': {color: ERROR},
}

/**
 * A Select's menu is PORTALLED to document.body, so it renders outside this
 * drawer's subtree and `fieldSx`'s descendant selectors — which reach only the
 * closed trigger — never touch it. Left alone its items take
 * theme.typography.body1's 1rem, the same MUI leak fieldSx beats one level
 * further out, and the list opens a third larger than every other dropdown in
 * the app. The same shape as disputes-v2/FilterBar.js's own `selectMenuProps`,
 * at the same SIZE['2xs'], so an open menu here matches every other menu a
 * merchant meets in this module.
 */
const selectMenuProps = {
  PaperProps: {sx: {'& .MuiMenuItem-root': {fontFamily: FONT, fontSize: SIZE['2xs']}}},
}

/**
 * A numbered step heading, the shape the Weight Dispute module's own evidence
 * flow uses. The number is decorative — the heading beside it already names the
 * step — so it is marked `aria-hidden` rather than read out twice.
 *
 * No approval percentage rides on this badge. The dispute screens print "97%
 * approved" / "52% approved" beside the same two steps, but nothing in this
 * repo sources those figures and they describe a different process; a freeze
 * request is a proposal about what a SKU weighs, not a contested bill. The
 * video step says only that it is recommended.
 */
function StepHead({n, title, chip}) {
  return (
    <Box sx={{display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 0.75}}>
      <Box
        aria-hidden
        sx={{
          width: 20, height: 20, flexShrink: 0, borderRadius: '6px',
          bgcolor: withOpacity(PRIMARY_BLUE, 0.12), color: PRIMARY_BLUE,
          display: 'grid', placeItems: 'center',
          fontFamily: FONT, fontSize: SIZE['2xs'], fontWeight: WEIGHT.bold,
        }}
      >
        {n}
      </Box>
      <Typography sx={{fontFamily: FONT, fontWeight: WEIGHT.bold, fontSize: SIZE.xs, color: NAVY}}>
        {title}
      </Typography>
      {chip && <Chip size='small' label={chip} sx={chipSx('ok')} />}
    </Box>
  )
}

/**
 * Widens the charged-weight distribution from the DISPUTED shipments to the
 * SKU's whole shipment count.
 *
 * WHERE THE REMAINDER GOES:
 *
 *   modal slab count = its real disputed count + (sku.shipments - covered.length)
 *
 * Every other slab keeps its real disputed count. The weights themselves are the
 * real observed ones either way; this moves counts and nothing else.
 *
 * THE RULE, STATED HONESTLY: THIS IS A PLACEMENT, NOT A FINDING. Nothing in this
 * module records what a never-disputed shipment was billed — the dataset holds
 * disputed rows and nothing else — so the remainder's charged weight is simply
 * unknown. It is drawn against the modal slab because that is the SKU's
 * most-charged weight and there is nowhere better to put an unknown, and
 * SlabColumns draws that portion HATCHED, prints the real disputed count under
 * every total and names the projection in the line beneath the chart's heading,
 * so it can never be read as observed.
 *
 * THE RULE THIS USED TO CLAIM IS CONTRADICTED BY THE DATA. It read: "a shipment
 * that is not in dispute was never flagged as mis-charged, so it billed at the
 * modal weight." But the modal APPLIED weight is itself an over-charge. Replayed
 * over the frozen dataset: 120 of 120 rows carry appliedWeight > originalWeight
 * (0 equal, 0 below); on 25 of 25 SKU groups the modal applied weight sits
 * strictly above the SKU's declared modal weight (0.75 -> 1.00 on 23, 1.00 ->
 * 1.50 on 2); and 25 of 25 modal slabs carry non-zero excess (excessAmount is
 * only ever 68 or 136). A shipment billed at the modal weight was therefore
 * over-charged, and by the dataset's own construction — data/noberoDisputes.js,
 * "Every row is a genuine over-modal charge" — an over-charge IS a dispute row.
 * So the remainder cannot have billed there, and no justification is offered for
 * the placement beyond the one above.
 *
 * EVERY slab, modal included, keeps its real `excess` — only a disputed shipment
 * carries excess freight, so padding the count must not pad the money. That
 * leaves the modal column's count and its rupees on two different denominators,
 * which SlabColumns states rather than leaves to be inferred.
 *
 * THE TOTAL IS NOT REAL DATA EITHER. `sku.shipments` is fabricated upstream from
 * a hash of the SKU code (state/analytics.js's `fabricatedShipments`) because the
 * dispute dataset records no shipment volume at all.
 *
 * Falls back to the real distribution UNTOUCHED, by identity, when the total is
 * missing, is not above the covered count, or when no covered shipment was
 * billed at the modal weight — a negative remainder would draw a bar shorter
 * than the data it already has, and a zero one would only rebuild the same array.
 */
export function projectSlabs(slabs, {modal, shipments, coveredCount}) {
  const real = slabs || []
  const rest = Number(shipments) - (Number(coveredCount) || 0)
  const at = real.findIndex((s) => s.weight === modal)
  // A missing total needs no branch of its own: Number(undefined) is NaN and
  // every comparison against NaN is false, so it fails `rest > 0` like the rest.
  if (at < 0 || !(rest > 0) || !Number.isFinite(rest)) return real
  return real.map((s, i) => (i === at ? {...s, count: s.count + rest} : s))
}

/**
 * The whole form. Everything the merchant can change lives here rather than in
 * `SkuFreezeDrawer` so that the Drawer can stay mounted for its exit transition
 * while the body unmounts on close — see the note on the export below.
 */
function FreezeBody({sku, rows, onClose, onRequestFreeze, onImageClick, fetchSkuHistory}) {
  const [level, setLevel] = React.useState(() => (sku.sku ? LEVEL.SKU : LEVEL.SUBCAT))
  // Opens on the group this SKU is catalogued under, which is also the only
  // option unless the covered shipments disagree with each other.
  const [subcat, setSubcat] = React.useState(() => subcatOf(sku))
  const [touched, setTouched] = React.useState(false)
  const [dead, setDead] = React.useState('')
  const [packId, setPackId] = React.useState('')
  // The chosen material's spec, overridable. A merchant's actual carton drifts
  // from the catalogue entry it was bought as, and the freeze is granted against
  // what they actually ship in. Seeded from the catalogue on every pick and
  // never written back to it — PACKS is a shared frozen list and packs.test.js
  // guards it against the freeze module's copy.
  const [packSelfG, setPackSelfG] = React.useState('')
  const [dimL, setDimL] = React.useState('')
  const [dimB, setDimB] = React.useState('')
  const [dimH, setDimH] = React.useState('')
  const [declared, setDeclared] = React.useState(false)
  const [video, setVideo] = React.useState(null)
  const [images, setImages] = React.useState({})
  const [fileErrors, setFileErrors] = React.useState({})
  const [dragKey, setDragKey] = React.useState(null)

  const key = skuKey(sku)
  const skuRows = React.useMemo(() => (rows || []).filter((r) => skuKey(r) === key), [rows, key])

  // The same expression the freeze module's own raise drawer uses: the level
  // NARROWS what is already in front of the merchant, it never reaches past it.
  // What widens at sub-category level is the AGREEMENT, and the note under the
  // select is where that reach is counted out.
  const covered = level === LEVEL.SUBCAT ? skuRows.filter((r) => subcatOf(r) === subcat) : skuRows

  const subOptions = React.useMemo(() => subcatOptions(sku, skuRows), [sku, skuRows])
  const reach = subcatReach(rows, subcat)

  const coverKey = covered.map((r) => r.id).join('|')

  // Ascending by weight, so the lightest and heaviest slabs are its two ends.
  // The count-first `histogramOf` is not needed here — deriveProposal builds
  // its own to find the mode.
  const realSlabs = React.useMemo(() => slabBreakdown(covered), [coverKey]) // eslint-disable-line react-hooks/exhaustive-deps
  // Hoisted above the chart because the projection needs to know which slab is
  // the modal one before it can decide where the remainder lands.
  const seeded = React.useMemo(() => deriveProposal(sku, covered), [coverKey]) // eslint-disable-line react-hooks/exhaustive-deps

  // What the chart plots: the SKU's whole shipment count, not only the disputed
  // part of it. projectSlabs states the rule and names what in it is fabricated.
  const slabs = projectSlabs(realSlabs, {
    modal: seeded.chargeKg, shipments: sku.shipments, coveredCount: covered.length,
  })
  // Read back off the bars rather than recomputed from `sku.shipments`, so the
  // heading can never name a total the columns do not add up to — including on
  // the fallback path, where it drops back to the covered count on its own.
  const plotted = slabs.reduce((n, x) => n + x.count, 0)
  // Unaffected by the projection, which never touches `excess`.
  const totalExcess = slabs.reduce((n, x) => n + x.excess, 0)
  const pack = packOf(packId)

  // Reseeds on every pick rather than latching: picking a different material is
  // an explicit statement that the previous one's figures no longer apply, so
  // carrying an edit across would silently attach a flyer's self weight to a
  // carton. Within one material the fields are the merchant's to change.
  const choosePack = (id) => {
    const next = packOf(id)
    setPackId(id)
    setPackSelfG(next && next.selfWeightG != null ? String(next.selfWeightG) : '')
  }

  // DERIVED, not typed. Volumetric is what the box works out to and chargeable
  // is the higher of the two — both are arithmetic, and letting a merchant type
  // them was what made the old mismatch warning necessary at all. With the pair
  // computed the two can no longer disagree, so the warning and its 'Use X kg'
  // button are gone with them.
  const vol = volumetricKg(dimL, dimB, dimH)
  const charge = chargeableKg(Number(dead) || 0, vol)
  const DIMENSION_FIELDS = [
    ['Length', dimL, setDimL],
    ['Breadth', dimB, setDimB],
    ['Height', dimH, setDimH],
  ]
  const lo = slabs.length ? slabs[0].weight : 0
  const hi = slabs.length ? slabs[slabs.length - 1].weight : 0

  const facts = weightFacts(dead, vol, charge)
  // Kept although its section is gone: the submit gate and the evidence
  // scope both read `blocked`, so removing the display removed the explanation,
  // not the rule. The footer sentence is what names the shortfall now.
  const partition = partitionRows(covered)

  // Shaped ONCE per render and reused by both the submit gate and the sentence
  // the footer prints, so the set the gate opened on can never be a different
  // set from the one the request is described by. hasAttachedEvidence reads
  // this immutable {video, images[]} shape, never the live `images` state.
  const files = buildEvidenceFiles({video, images})
  const attachedEvidence = hasAttachedEvidence(files)

  const gaps = gapsFor({
    level,
    subcat,
    covered,
    blocked: partition.blocked,
    deadKg: Number(dead),
    volKg: Number(vol),
    chargeKg: Number(charge),
    packing: packId,
    packSpec: {selfWeightG: packSelfG},
    declared,
    attachedEvidence,
  })
  const sentence = gapSentence(gaps)
  const scope = evidenceScope({
    coveredCount: covered.length, blockedCount: partition.blocked.length, attached: attachedEvidence,
  })


  /**
   * THE ONE LATCH. Any edit to dead/volumetric/chargeable sets it, and once set
   * this effect never re-derives anything again — a per-field dirty flag would
   * let a level switch silently overwrite a weight the merchant just typed. The
   * dependency array is deliberately [coverKey], a STRING, so this fires when
   * the SET of covered shipments changes, not when their object identities do.
   *
   * It also runs on mount, which is how the drawer opens pre-filled: FreezeBody
   * is keyed on the SKU, so a new SKU is a new mount and there is no reset-on-
   * reopen effect to write.
   */
  React.useEffect(() => {
    if (touched || !covered.length) return
    setDead(String(seeded.deadKg))
    // The courier's own measured carton on the sample shipment. Seeding it
    // reproduces `seeded.volKg` exactly — l x b x h / 5000 on 41 x 22 x 5.54 is
    // the 1.00 kg that shipment was billed at — so the derived pair below opens
    // on the same figures the old typed pair was seeded with.
    setDimL(seeded.dims ? String(seeded.dims.l) : '')
    setDimB(seeded.dims ? String(seeded.dims.b) : '')
    setDimH(seeded.dims ? String(seeded.dims.h) : '')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [coverKey])

  function markTouched(setter) {
    return (event) => { setter(event.target.value); setTouched(true) }
  }

  // Tracked in a ref rather than in state, so the unmount cleanup still sees
  // every url this drawer ever minted even if the last render had dropped it.
  //
  // DIFFERENT FROM THE FREEZE ORIGINAL, deliberately: that drawer stops tracking
  // the submitted urls on submit, because its payload hands them to a request
  // store that outlives the drawer. `onRequestFreeze(key, chargeKg)` carries no
  // files, so nothing here outlives the drawer and every url is revoked on
  // unmount — freeze's `handedOn` filter is not copied. Nothing renders a
  // preview from a blob url in this drawer (DropZone shows the file NAME), so
  // revoking while the Drawer is still transitioning out has no visible effect.
  const blobUrls = React.useRef([])
  const trackUrl = (file) => {
    const url = URL.createObjectURL(file)
    blobUrls.current.push(url)
    return url
  }
  const releaseUrl = (url) => {
    if (!url) return
    URL.revokeObjectURL(url)
    blobUrls.current = blobUrls.current.filter((u) => u !== url)
  }
  React.useEffect(() => () => {
    blobUrls.current.forEach((u) => URL.revokeObjectURL(u))
    blobUrls.current = []
  }, [])

  // `zoneKey` rather than the original's `key`: in this drawer `key` is already
  // the SKU key the request is raised against, and two different keys under one
  // name in the same scope is how the wrong one gets read.
  const setFileError = (zoneKey, message) => setFileErrors((prev) => {
    const next = {...prev}
    if (message) next[zoneKey] = message; else delete next[zoneKey]
    return next
  })

  const takeVideo = (file) => {
    const check = validateVideo(file)
    if (!check.ok) {
      setFileError('video', check.message)
      return
    }
    setFileError('video', '')
    setVideo((prev) => {
      if (prev) releaseUrl(prev.url)
      return {name: file.name, size: file.size, url: trackUrl(file)}
    })
  }

  const clearVideo = () => setVideo((prev) => {
    if (prev) releaseUrl(prev.url)
    return null
  })

  // Replacing a file in a slot has to revoke the url the old one held, or that
  // blob stays alive for the life of the tab.
  const takeImage = (slotKey, file) => {
    const check = validateImage(file)
    if (!check.ok) {
      setFileError(slotKey, check.message)
      return
    }
    setFileError(slotKey, '')
    setImages((prev) => {
      if (prev[slotKey]) releaseUrl(prev[slotKey].url)
      return {...prev, [slotKey]: {name: file.name, size: file.size, url: trackUrl(file)}}
    })
  }

  const clearImage = (slotKey) => setImages((prev) => {
    if (!prev[slotKey]) return prev
    releaseUrl(prev[slotKey].url)
    const next = {...prev}
    delete next[slotKey]
    return next
  })

  const pickFrom = (event) => {
    const file = event.target.files && event.target.files[0]
    event.target.value = ''
    return file || null
  }

  // One `dragKey` for all six zones, keyed-compared: that is what stops the
  // pointer crossing from one slot into its neighbour leaving both lit.
  const dragOver = (zoneKey) => (event) => { event.preventDefault(); setDragKey(zoneKey) }
  const dragLeave = (zoneKey) => (event) => {
    event.preventDefault()
    setDragKey((k) => (k === zoneKey ? null : k))
  }
  const dropOn = (zoneKey, take) => (event) => {
    event.preventDefault()
    setDragKey(null)
    const file = event.dataTransfer.files && event.dataTransfer.files[0]
    if (file) take(file)
  }

  function handleSubmit() {
    // Re-checked here, not just at the disabled-button boundary: the button can
    // be reached by keyboard while state is mid-update.
    if (gaps.length) return
    // The ask goes out at the CHARGEABLE figure in the form, not at the seeded
    // suggestion: the merchant can move it, and a button that kept naming the
    // seeded number would submit something other than what it says.
    //
    // The payload is deliberately just the key and the weight. index.js's
    // handleRequestFreeze is a snackbar — this module has no request store, and
    // shaping a fuller request body here would either invent a record nothing
    // reads or reach into freeze-v2, which the two modules are kept either side
    // of. `level` and the uploaded files are therefore
    // session-only, and the blob urls are revoked on unmount rather than handed on.
    if (onRequestFreeze) onRequestFreeze(key, Number(charge))
    onClose()
  }

  return (
    // `p: 2.5` / `gap: 2.5`, not the freeze drawer's `p: 2` / `GAP.lg`: this is
    // the padding rhythm every other disputes-v2 drawer uses, and what the user
    // asked to match is the WIDTH and the density of detail (D1), not freeze's
    // padding scale.
    <Box sx={{display: 'flex', flexDirection: 'column', height: '100%'}}>
      <Box sx={{
        p: 2.5, borderBottom: `1px solid ${BORDER}`, display: 'flex',
        justifyContent: 'space-between', alignItems: 'flex-start', gap: 1,
      }}
      >
        <Box sx={{minWidth: 0}}>
          <Typography sx={{fontFamily: FONT, fontWeight: WEIGHT.extra, fontSize: SIZE.md, color: NAVY}}>
            Freeze SKU
          </Typography>
          {/* Identity only. The shipment count and the product name are
              details, and they live in the summary panel beside the chart —
              printing them here as well was the same fact twice. */}
          <Typography
            sx={{...labelSx, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}
            title={key}
          >
            {sku.sku || sku.product || '(no SKU code)'}
          </Typography>
          {/* The old drawer said "Evidence attaches to the SKU, saved against the
              AWB below." That sentence is now false — evidence no longer goes
              through EvidenceIntake and buildEvidenceFiles records no shipment
              id — so it is replaced rather than carried over. */}
          <Typography sx={{...subtleTextSx, mt: 1, lineHeight: 1.5}}>
            Agree one charged weight for this SKU.
          </Typography>
        </Box>
        <IconButton onClick={onClose} aria-label='Close, without requesting a freeze' size='small'>
          <CloseIcon fontSize='small' />
        </IconButton>
      </Box>

      <Box sx={{p: 2.5, overflowY: 'auto', flex: 1, display: 'grid', gap: 2.5}}>
        {/* SECTION 1 — non-dismissible and first: the condition the whole freeze
            hangs on belongs before the merchant proposes a weight, not after. */}
        <Alert severity='warning' sx={alertSx}>
          Every weight freeze is conditional on the declared packing material being used. Change
          the material and the freeze does not hold.
        </Alert>

        {/* SECTION 2. The freeze original lists every selected row; this one
            plots the distribution instead. A per-shipment table answers "which
            AWB", a question the ledger tab already answers and one nobody asks
            while proposing a weight — what matters here is how badly the slabs
            disagree, which is the whole case for freezing one. The heading counts
            whatever the chart plots, which projectSlabs has already widened from
            the disputed shipments to the SKU's whole set. */}
        <Box sx={{border: `1px solid ${BORDER}`, borderRadius: '10px', p: 2}}>
          <Typography sx={{...sectionLabelSx, mb: 1.5}}>
            How these {int(plotted)} {shipWord(plotted)} were charged
          </Typography>
          {/* Chart and figures either side of one rule, inside one card, so the
              block reads as a single summary rather than a chart with a box
              parked next to it.

              The split happens at `sm`, the SAME breakpoint the Drawer's own
              width uses. Keying it to `md` was wrong: the drawer is already 660
              wide at `sm`, so on a viewport between those two the panel dropped
              below the chart and left the dead space it was meant to fill. */}
          <Box sx={{
            display: 'grid',
            // The chart is sized by its bars and the LIST takes everything else.
            // Three other splits were tried and each left a hole somewhere: a
            // flexible chart strands two bars against empty track, sharing that
            // track between them parks the bars 145px apart, and pairing the
            // list into two columns leaves the shorter one hanging. A wide
            // label/value list has no such failure mode — it just reads as a
            // summary table, and it ends where the card ends.
            gridTemplateColumns: {xs: '1fr', sm: 'auto minmax(0, 1fr)'},
            gap: 2.5,
            alignItems: 'start',
          }}
          >
            <SlabColumns slabs={slabs} modal={seeded.chargeKg} />

            <Box sx={{
              borderLeft: {xs: 'none', sm: `1px solid ${BORDER}`},
              borderTop: {xs: `1px solid ${BORDER}`, sm: 'none'},
              pl: {xs: 0, sm: 2.5}, pt: {xs: 1.5, sm: 0},
            }}
            >
              {/* One slab means no range and no average worth printing: 'from'
                  would promise a spread that is not there, and the mean of a
                  single value only restates the row above it. */}
              <KeyValue items={[
                {label: 'SKU', value: sku.sku || '—'},
                ...(sku.product && sku.product !== sku.sku
                  ? [{label: 'Product', value: sku.product}]
                  : []),
                // The SKU's lifetime total, the same figure the Recommended
                // weight corrections table prints for this row, so the two
                // surfaces agree. FABRICATED upstream — see analytics.js's
                // fabricatedShipments — because the dispute dataset holds no
                // real shipment volume.
                //
                // The chart beside this panel now plots that same total, so this
                // row and the section heading agree by construction — except on
                // projectSlabs' fallback path, where the heading drops to the
                // covered count and this row still states the SKU's own.
                {label: 'Shipments', value: int(sku.shipments)},
                slabs.length > 1
                  ? {label: 'Charged from', value: `${fmtKg(lo)} to ${fmtKg(hi)}`}
                  : {label: 'Charged at', value: fmtKg(lo)},
                {label: 'Most probable', value: fmtKg(seeded.chargeKg)},
                // The split behind the most probable weight, from the one
                // covered shipment that charged it. Grouped under it rather
                // than sorted among the aggregates, because they describe that
                // single shipment and the rows above describe all of them.
                {label: 'Dead weight', value: fmtKg(seeded.deadKg)},
                {label: 'Volumetric weight', value: fmtKg(seeded.volKg)},
                {label: 'Dimensions', value: dimsText(seeded.dims)},
                {label: 'Excess freight', value: fmtCurrency(totalExcess)},
              ]}
              />
            </Box>
          </Box>
        </Box>

        {/* SECTION 2b — live mode only. The chart above is built from DISPUTED
            rows; this is the SKU's actual recent bookings from the weight
            ledger (D17: the drawer shows the last 50), so a merchant proposing a
            weight can see what the courier billed on shipments nobody disputed.
            The seeded harness has no ledger and passes no seam, so the section
            is absent there rather than an empty promise. */}
        {typeof fetchSkuHistory === 'function' && sku.sku && (
          <LedgerHistory sku={sku.sku} merchantId={singleMerchantOf(skuRows)} fetchSkuHistory={fetchSkuHistory} />
        )}

        {/* SECTION 3 */}
        <Box component='fieldset' sx={{border: `1px solid ${BORDER}`, borderRadius: '10px', p: 1.5, m: 0}}>
          <Typography component='legend' sx={sectionLabelSx}>Freeze at which level</Typography>
          <RadioGroup
            value={level}
            onChange={(event) => setLevel(event.target.value)}
            sx={{display: 'grid', gridTemplateColumns: {xs: '1fr', sm: 'repeat(2, 1fr)'}, gap: 1}}
          >
            {LEVELS.map((lv) => {
              const disabled = levelDisabled(lv.v, sku)
              const label = lv.l + (lv.v === LEVEL.SKU && !disabled ? ' (recommended)' : '')
              return (
                <Box
                  key={lv.v}
                  sx={{
                    border: `1px solid ${level === lv.v ? PRIMARY_BLUE : BORDER}`,
                    borderRadius: '8px', p: 1, opacity: disabled ? 0.55 : 1,
                  }}
                >
                  <FormControlLabel
                    value={lv.v}
                    disabled={disabled}
                    control={<Radio size='small' />}
                    label={(
                      <Typography sx={{fontFamily: FONT, fontWeight: WEIGHT.semibold, fontSize: SIZE.xs, color: NAVY}}>
                        {label}
                      </Typography>
                    )}
                    sx={{m: 0, alignItems: 'flex-start'}}
                  />
                  <Typography sx={{...labelSx, ml: 3.5, display: 'block'}}>
                    {disabled ? levelDisabledReason() : lv.why}
                  </Typography>
                </Box>
              )
            })}
          </RadioGroup>

          {/* The group the freeze would be agreed for, named rather than
              assumed. Required, and gated through GAP.SUBCAT: at sub-category
              level an unnamed group is a freeze with no subject. */}
          {level === LEVEL.SUBCAT && (
            <Box sx={{display: 'grid', gap: 1, mt: 1.5}}>
              <TextField
                select required size='small' label='Sub-category' value={subcat}
                onChange={(event) => setSubcat(event.target.value)}
                helperText='Taken from the catalogue, by SKU code.'
                sx={{...fieldSx, maxWidth: 320}}
                SelectProps={{MenuProps: selectMenuProps}}
              >
                {subOptions.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
              </TextField>

              {/* A real membership check, not decoration. Every shipment under
                  one `sku || product` key shares one code today, and a
                  sub-category is read off the code, so this cannot fire on the
                  frozen dataset — the same standing this module's other
                  cross-checks have. It fires the day one key covers two codes,
                  which is exactly when a merchant would otherwise be shown a
                  freeze quietly narrower than the rows above it. */}
              {subOptions.length > 1 && (
                <Alert severity='info' sx={alertSx}>
                  These shipments span {int(subOptions.length)} sub-categories. A sub-category freeze
                  covers one at a time, so this request covers the {int(covered.length)} of{' '}
                  {int(skuRows.length)} {shipWord(skuRows.length)} in {subcat}.
                </Alert>
              )}

              {subcat !== UNCATEGORISED && reach.skus > 1 && (
                <Alert severity='info' sx={alertSx}>
                  A {subcat} freeze is agreed for the whole sub-category, not for this code alone —{' '}
                  {int(reach.skus)} disputed SKUs and {int(reach.shipments)}{' '}
                  disputed {shipWord(reach.shipments)} in view fall in it. The weight proposed below is
                  derived from the {int(covered.length)} on this SKU.
                </Alert>
              )}

              {subcat === UNCATEGORISED && (
                <Alert severity='warning' sx={alertSx}>
                  &apos;Uncategorised&apos; is a placeholder rather than a real sub-category — it is what a
                  shipment gets when its SKU code is not in the catalogue — so a freeze agreed against it
                  would reach every uncategorised SKU.{' '}
                  {levelDisabled(LEVEL.SKU, sku)
                    ? 'These shipments carry no SKU code either, so there is nothing narrower to agree one '
                      + 'against. Map them in Catalogue first.'
                    : 'Map this SKU in Catalogue first, or agree this freeze at SKU level instead.'}
                </Alert>
              )}
            </Box>
          )}
        </Box>

        {/* SECTION 4 */}
        <Box>
          <Typography sx={sectionLabelSx}>Packing material</Typography>
          {/* A picker over the merchant's real catalogue (data/packs.js), with
              NOTHING pre-selected: no dispute row records what it was packed
              in, so choosing one for them would put words in their mouth on the
              one declaration the freeze is granted against. */}
          <TextField
            select required fullWidth size='small'
            label='Packing material used for these shipments'
            value={packId}
            onChange={(event) => choosePack(event.target.value)}
            helperText='The freeze is granted against this material and nothing else.'
            sx={fieldSx}
            SelectProps={{MenuProps: selectMenuProps}}
          >
            {PACKS.map((p) => (
              <MenuItem key={p.id} value={p.id}>{packOptionLabel(p)}</MenuItem>
            ))}
          </TextField>

          {/* Self weight only. This card used to carry length, breadth and
              height too, which was a SECOND dimension triple in the same drawer
              — the merchant's box measured twice, with only one of the two
              driving the bill. The dimensions live with the product weight
              below, and the drawing that visualised them went with them. */}
          {pack && (
            <Box sx={{mt: 1.5, maxWidth: {sm: 220}}}>
              <TextField
                required fullWidth
                label='Self weight' size='small' type='number' value={packSelfG}
                onChange={(event) => setPackSelfG(event.target.value)}
                inputProps={{step: 1, min: 0}}
                InputProps={{endAdornment: (
                  <InputAdornment position='end'><Typography sx={labelSx}>g</Typography></InputAdornment>
                )}}
                sx={fieldSx}
              />
            </Box>
          )}

        </Box>

        {/* SECTION 5. Weight and size in one place, in the order the figures
            actually depend on each other: the product's own dead weight, then
            the box it ships in, then the two the courier derives from those.

            Volumetric and chargeable are READ-ONLY because they are arithmetic.
            They used to be typed alongside dead weight, which meant a merchant
            could propose a chargeable weight their own box contradicted — the
            reason the drawer carried a mismatch warning and a 'Use 1.30 kg'
            button. Deriving them deletes that whole class of disagreement. */}
        <Box>
          <Typography sx={{...sectionLabelSx, mb: 1.25}}>Product weight and dimensions</Typography>

          <Box sx={{
            display: 'grid',
            gridTemplateColumns: {xs: '1fr', sm: 'auto minmax(0, 1fr)'},
            gap: 2,
            alignItems: 'start',
          }}
          >
            {/* The box these figures describe, drawn from the triple beside it,
                so correcting a side redraws it. This lived on the packing-material
                card while the dimensions did; it followed them here. A material
                with no complete box — a flyer, or a carton mid-edit — gets the
                sleeve rather than a drawing of nothing. */}
            <Box sx={{flexShrink: 0, width: 96, textAlign: 'center'}}>
              {vol > 0
                ? <CartonSVG dims={[Number(dimL), Number(dimB), Number(dimH)]} size={96} />
                : <SleeveSVG size={96} />}
              <Typography sx={{...labelSx, display: 'block', mt: 0.25}}>Not to scale</Typography>
            </Box>

            <Box sx={{display: 'grid', gap: 1.5, minWidth: 0}}>
          <Box sx={{display: 'grid', gridTemplateColumns: {xs: '1fr', sm: 'repeat(4, 1fr)'}, gap: 1.5}}>
            {/* `required` on all four: they are what the freeze is granted at,
                and a field that blocks the ask has to say so here rather than
                only in the footer. MUI appends " *" to the accessible name it
                derives from `label`, so a test querying these by exact label
                has to match the asterisk too. */}
            <TextField
              required
              label='Dead weight' size='small' type='number' value={dead}
              onChange={markTouched(setDead)}
              inputProps={{step: 0.05, min: 0}}
              InputProps={{endAdornment: (
                <InputAdornment position='end'><Typography sx={labelSx}>kg</Typography></InputAdornment>
              )}}
              sx={fieldSx}
            />
            {DIMENSION_FIELDS.map(([label, value, set]) => (
              <TextField
                key={label}
                required
                label={label} size='small' type='number' value={value}
                onChange={markTouched(set)}
                inputProps={{step: 0.5, min: 0}}
                InputProps={{endAdornment: (
                  <InputAdornment position='end'><Typography sx={labelSx}>cm</Typography></InputAdornment>
                )}}
                sx={fieldSx}
              />
            ))}
          </Box>

          {/* The two the courier works out, stated as results rather than
              fields. 'Not yet' rather than 0.00 kg while the box is incomplete:
              a zero here would read as a measurement. */}
          <Box sx={{
            display: 'grid', gridTemplateColumns: {xs: '1fr', sm: 'repeat(2, 1fr)'}, gap: 1.5,
            mt: 1.5, p: 1.25, borderRadius: '8px', bgcolor: withOpacity(NAVY, 0.03),
          }}
          >
            <Typography sx={labelSx}>
              Volumetric weight{' '}
              <Box component='span' sx={{color: NAVY, fontWeight: WEIGHT.semibold}}>
                {vol > 0 ? fmtKg(vol) : 'Not yet'}
              </Box>
              <Box component='span' sx={{display: 'block'}}>Length × breadth × height ÷ {int(VOLUMETRIC_DIVISOR)}.</Box>
            </Typography>
            <Typography sx={labelSx}>
              Chargeable weight{' '}
              <Box component='span' sx={{color: NAVY, fontWeight: WEIGHT.semibold}}>
                {charge > 0 ? fmtKg(charge) : 'Not yet'}
              </Box>
              <Box component='span' sx={{display: 'block'}}>The higher of dead and volumetric. What the freeze bills at.</Box>
            </Typography>
          </Box>
            </Box>
          </Box>

          <Box sx={{display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mt: 1}}>
            {/* Volumetric governing is the case worth colouring: the bill is then
                driven by the packing material rather than by the product, which
                is the one thing a merchant can act on by repacking. */}
            {facts.known && (
              <Chip
                size='small'
                label={GOVERNS_SENTENCE[facts.governs](facts.higher)}
                sx={sentenceChipSx(facts.governs === 'vol' ? 'warn' : 'info')}
              />
            )}
            {covered.length > 0 && (
              <Typography sx={labelSx}>
                Most probable across these {shipWord(covered.length)}: {fmtKg(seeded.chargeKg)}.
              </Typography>
            )}
          </Box>
        </Box>

        {/* The declaration confirms every figure above it — the material, its
            self weight, the product weight and the box — so it sits after all
            of them rather than inside the packing section, which was only ever
            the first of the three. Boxed and tinted on tick because it is a
            gate rather than a preference: an unticked checkbox in a long form
            is easy to read past. */}
        <Box
          sx={{
            px: 1.5, py: 0.5, borderRadius: '10px',
            border: `1px solid ${declared ? SUCCESS : BORDER}`,
            bgcolor: declared ? withOpacity(SUCCESS, 0.08) : 'transparent',
          }}
        >
          <FormControlLabel
            sx={{display: 'flex', alignItems: 'flex-start', m: 0}}
            control={<Checkbox checked={declared} onChange={(event) => setDeclared(event.target.checked)} />}
            label={<Typography sx={{...labelSx, mt: 0.9}}>{DECLARATION}</Typography>}
          />
        </Box>

        {/* SECTION 6. The same two steps, and the same five named shots, the
            module's own Evidence Studio takes. This is the only place a merchant
            can record their own readings for a freeze, so it is what clears the
            evidence gap when a covered shipment has nothing on file.
            EvidenceIntake is deliberately NOT reused here (D6): it asks for
            L/B/H, dead weight, product category and a URL, which would duplicate
            sections 4 and 5 and would submit against one shipment id, which is
            the opposite of what a request-level attachment means. */}
        <Box>
          <Box sx={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            flexWrap: 'wrap', gap: 1,
          }}
          >
            <Typography sx={sectionLabelSx}>Add evidence</Typography>
          </Box>

          <Typography sx={{...labelSx, mb: scope.note ? 0.75 : 1.5, display: 'block'}}>
            Readings you record here travel with the request, not with any one shipment.
          </Typography>

          {scope.note && (
            <Typography sx={{...labelSx, mb: 1.5, display: 'block'}}>{scope.note}</Typography>
          )}

          <Box sx={{display: 'grid', gap: 2}}>
            <Box>
              <StepHead n={1} title='Packing video' chip='Recommended' />
              <DropZone
                icon={VideocamOutlinedIcon}
                big
                label='Add a packing video'
                hint={`One unbroken clip, ending on the scale. ${VIDEO_FORMATS} · up to ${mb(MAX_VIDEO_BYTES)}.`}
                file={video}
                error={fileErrors.video}
                dragActive={dragKey === 'video'}
                accept={Object.keys(VIDEO_TYPES).join(',')}
                ariaLabel='Upload the packing video'
                removeAriaLabel='Remove the packing video'
                onPick={(event) => { const f = pickFrom(event); if (f) takeVideo(f) }}
                onClear={clearVideo}
                onDragOver={dragOver('video')}
                onDragLeave={dragLeave('video')}
                onDrop={dropOn('video', takeVideo)}
              />
            </Box>

            <Box>
              <StepHead n={2} title='Photo evidence' />
              <Typography sx={{...labelSx, mb: 1, display: 'block'}}>
                Five shots, one fact each. Keep the measuring device and the parcel in the same frame.
                {IMAGE_FORMATS} · up to {mb(MAX_IMAGE_BYTES)} each.
              </Typography>
              <Box sx={{
                display: 'grid',
                gridTemplateColumns: {xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)'},
                gap: 1,
              }}
              >
                {SHOT_SLOTS.map((slot) => (
                  <DropZone
                    key={slot.k}
                    icon={ImageOutlinedIcon}
                    label={slot.label}
                    hint={slot.hint}
                    file={images[slot.k]}
                    error={fileErrors[slot.k]}
                    dragActive={dragKey === `img:${slot.k}`}
                    accept={Object.keys(IMAGE_TYPES).join(',')}
                    ariaLabel={`Upload the ${slot.label.toLowerCase()} image`}
                    removeAriaLabel={`Remove the ${slot.label.toLowerCase()} image`}
                    onPick={(event) => { const f = pickFrom(event); if (f) takeImage(slot.k, f) }}
                    onClear={() => clearImage(slot.k)}
                    onDragOver={dragOver(`img:${slot.k}`)}
                    onDragLeave={dragLeave(`img:${slot.k}`)}
                    onDrop={dropOn(`img:${slot.k}`, (f) => takeImage(slot.k, f))}
                  />
                ))}
              </Box>
            </Box>
          </Box>
        </Box>

        {/* SECTION 7. The two sentences before it are the standing rule — a
            raise is a proposal, and approval never outlives the declared packing
            material; the sentence itself is POLICY.preliminary, quoted verbatim
            from the source document rather than paraphrased. */}
        <Alert severity='info' sx={alertSx}>
          A merchant-raised freeze takes effect only after Prozo approves it. {CLOSING_NOTE}
        </Alert>
      </Box>

      <Box sx={{
        p: 2.5, borderTop: `1px solid ${BORDER}`, display: 'flex',
        alignItems: 'center', gap: 1.5, flexWrap: 'wrap',
      }}
      >
        <Typography sx={{...labelSx, flex: '1 1 260px', color: gaps.length ? WARNING : SUBTLE}}>
          {gaps.length
            ? sentence
            // `level` is printed as-is, not lower-cased the way the freeze
            // module does it: its levels are words, ours include the acronym
            // SKU, and 'at sku level' reads as a typo.
            : `Goes to Prozo for review at ${level} level, covering ${int(covered.length)} ${shipWord(covered.length)}.`}
        </Typography>

        <Button sx={secondaryBtnSx} onClick={onClose}>Cancel</Button>

        {gaps.length ? (
          // A disabled MUI Button fires no pointer events and takes no focus, so
          // the Tooltip needs a wrapper; the wrapper is focusable and carries
          // role='note' so a keyboard user can reach the reason and hear it.
          <Tooltip title={sentence}>
            <Box
              component='span'
              tabIndex={0}
              role='note'
              aria-label={'Requesting a weight freeze is unavailable. ' + sentence}
            >
              <Button
                variant='contained'
                disabled
                sx={primaryBtnSx}
                startIcon={<AcUnitOutlinedIcon sx={{fontSize: 16}} />}
              >
                Request weight freeze
              </Button>
            </Box>
          </Tooltip>
        ) : (
          <Button
            variant='contained'
            sx={primaryBtnSx}
            startIcon={<AcUnitOutlinedIcon sx={{fontSize: 16}} />}
            onClick={handleSubmit}
          >
            Request weight freeze at {fmtKg(Number(charge))}
          </Button>
        )}
      </Box>
    </Box>
  )
}

/**
 * The drawer's history window. The backend default is the same 50
 * (`com.proship.weightv2.sku.historyWindow`); it is passed explicitly so the
 * request says what it wants rather than relying on two defaults agreeing.
 */
export const HISTORY_LIMIT = 50

const fmtDay = (v) => {
  if (!v) return '—'
  const d = new Date(v)
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString('en-IN', {day: '2-digit', month: 'short', year: '2-digit'})
}
const kgOrDash = (v) => (v == null ? '—' : fmtKg(v))

const histCellSx = {
  fontFamily: FONT, fontSize: SIZE['2xs'], color: NAVY, py: 0.5, px: 0.75,
  borderBottom: `1px solid ${BORDER}`, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums',
}
const histHeadSx = {...histCellSx, color: SUBTLE, fontWeight: WEIGHT.bold, textTransform: 'uppercase', letterSpacing: '0.03em'}

/**
 * The SKU's most recent bookings from the weight ledger — every shipment, not
 * only the disputed ones the chart is built from. `fetchSkuHistory(sku, limit)`
 * is the seam index.js hands down in live mode (data/api.js's apiSkuHistory);
 * rows are `SkuHistoryRow`s (weightv2/pojo/WdV2Summary.java): awb, shipmentDate,
 * courier, declaredKg, pushedKg, billedKg (null until the courier MIS lands),
 * discrepancyKg, discrepancyLevel, stage.
 *
 * Loads once per mount. FreezeBody is keyed on the SKU, so a new SKU is a new
 * mount and a new fetch; the `alive` flag stops a late response from a closed
 * drawer writing into an unmounted component.
 */
function LedgerHistory({sku, merchantId, fetchSkuHistory}) {
  const [state, setState] = React.useState({status: 'loading', rows: []})

  React.useEffect(() => {
    let alive = true
    setState({status: 'loading', rows: []})
    Promise.resolve()
      // merchantId names the merchant the covered rows belong to; a merchant login does not
      // need it, a Prozo-admin login (cross-merchant scope) is refused by the server without it.
      .then(() => fetchSkuHistory(sku, HISTORY_LIMIT, merchantId))
      .then((rows) => { if (alive) setState({status: 'ready', rows: Array.isArray(rows) ? rows : []}) })
      .catch(() => { if (alive) setState({status: 'error', rows: []}) })
    return () => { alive = false }
  }, [sku, merchantId, fetchSkuHistory])

  const billed = state.rows.filter((r) => r.billedKg != null)
  const over = billed.filter((r) => Number(r.discrepancyKg) > 0).length

  return (
    <Box sx={{border: `1px solid ${BORDER}`, borderRadius: '10px', p: 2}}>
      <Typography sx={{...sectionLabelSx, mb: 0.25}}>Recent shipments of this SKU</Typography>
      <Typography sx={{...subtleTextSx, mb: 1.25, display: 'block'}}>
        {state.status === 'loading' && 'Loading the last ' + int(HISTORY_LIMIT) + ' bookings from the weight ledger…'}
        {state.status === 'error' && 'The weight ledger could not be reached just now. The chart above still stands.'}
        {state.status === 'ready' && state.rows.length === 0 && 'No bookings for this SKU code in the weight ledger yet.'}
        {state.status === 'ready' && state.rows.length > 0 && (
          'Last ' + int(state.rows.length) + ' ' + shipWord(state.rows.length) + ' booked under this code, newest first — '
          + 'every shipment, not only the disputed ones. '
          + (billed.length
            ? int(over) + ' of the ' + int(billed.length) + ' the courier has billed came back heavier than what was pushed.'
            : 'None of them has a courier bill on file yet.')
        )}
      </Typography>

      {state.status === 'ready' && state.rows.length > 0 && (
        <Box sx={{overflowX: 'auto', maxHeight: 260, overflowY: 'auto'}}>
          <Box component='table' sx={{borderCollapse: 'collapse', width: '100%'}}>
            <Box component='thead'>
              <Box component='tr'>
                {['Shipped', 'AWB', 'Courier', 'Declared', 'Pushed', 'Billed', 'Gap'].map((h) => (
                  <Box key={h} component='th' align='left' sx={histHeadSx}>{h}</Box>
                ))}
              </Box>
            </Box>
            <Box component='tbody'>
              {state.rows.map((r) => {
                const gap = r.billedKg == null || r.discrepancyKg == null ? null : Number(r.discrepancyKg)
                return (
                  <Box component='tr' key={r.awb}>
                    <Box component='td' sx={histCellSx}>{fmtDay(r.shipmentDate)}</Box>
                    <Box component='td' sx={{...histCellSx, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace'}}>{r.awb}</Box>
                    <Box component='td' sx={histCellSx}>{r.courier || '—'}</Box>
                    <Box component='td' sx={histCellSx}>{kgOrDash(r.declaredKg)}</Box>
                    <Box component='td' sx={histCellSx}>{kgOrDash(r.pushedKg)}</Box>
                    <Box component='td' sx={histCellSx}>{kgOrDash(r.billedKg)}</Box>
                    <Box
                      component='td'
                      sx={{...histCellSx, color: gap > 0 ? WARNING : gap === 0 ? SUCCESS : SUBTLE, fontWeight: gap > 0 ? WEIGHT.semibold : WEIGHT.regular}}
                    >
                      {gap == null ? '—' : (gap > 0 ? '+' : '') + fmtKg(gap)}
                    </Box>
                  </Box>
                )
              })}
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  )
}

/**
 * A flat sleeve, for a packing material that declares no box.
 *
 * CartonSVG needs three positive sides and correctly refuses to draw without
 * them, which left the pack card with no illustration at all for a flyer or a
 * paper wrap — and none for a carton until the merchant had typed all three
 * dimensions. This fills that slot with a shape that says "soft pack" rather
 * than inventing a box: no isometric depth, one fold line, muted.
 *
 * It is an ILLUSTRATION, not a measurement. It never scales to anything, which
 * is why the caption beside it reads the same for both shapes.
 */
function SleeveSVG({size = 96}) {
  const w = size
  const h = size * 0.72
  return (
    <Box
      component='svg'
      role='img'
      aria-label='Soft pack, no declared box'
      viewBox={`0 0 ${w} ${h}`}
      sx={{width: w, height: h, display: 'block'}}
    >
      <rect
        x={w * 0.12} y={h * 0.14} width={w * 0.76} height={h * 0.72}
        rx='6' fill={withOpacity(MUTED, 0.14)} stroke={MUTED} strokeWidth='1.5'
      />
      <path
        d={`M ${w * 0.12} ${h * 0.36} L ${w * 0.88} ${h * 0.36}`}
        stroke={MUTED} strokeWidth='1.5' strokeDasharray='3 3' fill='none'
      />
    </Box>
  )
}

/**
 * The charged-weight slabs as vertical columns — one per 0.5 kg step the courier
 * actually billed, lightest on the left, height proportional to how many
 * shipments landed there.
 *
 * Columns, not the horizontal bars this started as: a horizontal ladder needs a
 * full row per slab and a gutter wide enough for the longest bar, which pushed
 * the block past half the drawer and left the panel beside it nothing to sit in.
 * Columns are the natural orientation for a distribution anyway — the eye reads
 * a skyline in one pass — and the whole chart is a FIXED height whatever the
 * slab count, so this section stops growing with the data.
 *
 * Fixed 76px columns rather than fractions. Flexible ones render two slabs as
 * two absurd 200px blocks, and merely centring a capped bar inside a flexible
 * column parks two bars 145px apart. A bar chart's job is to be compared, which
 * needs the bars near each other; the space they do not use belongs to the
 * summary list beside them, not to the gaps between them.
 *
 * No axis and no gridlines. Every value is printed on the column itself, which
 * is both the honest presentation and the accessible one — the bars are
 * `aria-hidden` because the numbers beside them are real text.
 *
 * NO MONEY ON THE COLUMNS. Each slab used to print its own excess freight
 * beneath the weight, which was true while the bars counted disputed shipments
 * only. Once the counts were projected to the SKU's whole shipment set the two
 * stopped describing the same population: the modal column read "172" over the
 * 4 disputed shipments' worth of excess, and the 4-shipment column beside it
 * carried twice the rupees. Excess belongs to the disputed rows alone, so it is
 * stated once, as a total, in the summary panel — where nothing implies it is
 * spread across every shipment in the bar above it.
 *
 * The MODAL slab carries the primary colour and everything else is amber, the
 * same rule SkuSpark and the freeze module's IniSpark use, so "amber means
 * billed at something else" holds wherever a merchant meets it. Here the blue
 * column is also the proposal, which the caption below the chart states rather
 * than leaving to be inferred from a hue.
 */
const BAR_AREA = 78
const BAR_W = 76

function SlabColumns({slabs, modal}) {
  const max = slabs.reduce((m, x) => Math.max(m, x.count), 0) || 1

  return (
    <Box sx={{display: 'flex', gap: 1.5, alignItems: 'flex-end'}}>
      {slabs.map((slab) => {
        const isModal = slab.weight === modal
        return (
          <Box key={slab.weight} sx={{width: BAR_W, textAlign: 'center'}}>
            <Typography
              sx={{
                fontFamily: FONT, fontSize: SIZE.xs, fontWeight: WEIGHT.bold,
                fontVariantNumeric: 'tabular-nums', color: NAVY, mb: 0.25,
              }}
            >
              {int(slab.count)}
            </Typography>

            {/* The track is the constant: bars grow up from a shared baseline,
                so their tops are comparable across columns.

                The 4px floor is load-bearing now that the modal slab carries the
                SKU's whole non-disputed remainder: on the first row that is 172
                against 4, and 4/172 of a 78px track is 1.8px — below the floor,
                so the off-modal slab renders at 4px instead of dissolving into
                the baseline rule. The exact count sits in bold above every
                column regardless, so no value depends on bar height to be read. */}
            <Box aria-hidden sx={{height: BAR_AREA, display: 'flex', alignItems: 'flex-end'}}>
              <Box sx={{
                width: '100%',
                height: Math.max(4, (slab.count / max) * BAR_AREA),
                borderRadius: '4px 4px 0 0',
                bgcolor: isModal ? PRIMARY_BLUE : WARNING,
              }}
              />
            </Box>

            <Box sx={{
              borderTop: `2px solid ${isModal ? PRIMARY_BLUE : BORDER}`, pt: 0.5, mt: '-1px',
            }}
            >
              <Typography
                sx={{
                  fontFamily: FONT, fontSize: SIZE['2xs'],
                  fontWeight: isModal ? WEIGHT.bold : WEIGHT.semibold,
                  fontVariantNumeric: 'tabular-nums', color: isModal ? NAVY : SUBTLE,
                }}
              >
                {fmtKg(slab.weight)}
              </Typography>
            </Box>
          </Box>
        )
      })}
    </Box>
  )
}

/**
 * The "Freeze SKU" drawer for the SKU Weight Intelligence tab, at parity with
 * the freeze module's own Raise-weight-freeze-request drawer: same width, same
 * twelve-section structure, same density of detail — but every figure in it is
 * sourced from disputes-v2's own rows, and every string that named something
 * this module does not have (a DWS sorter) has been rewritten rather than
 * carried over. The two the module HAS since acquired — a pack catalogue and a
 * sub-category — it holds as its own mirrors under `data/`, each with a drift
 * guard, never by importing across the module boundary.
 *
 * WIDTH is `{xs: '100%', sm: 660, md: 720}`. It is portalled to document.body,
 * so it sits outside the page shell's `zoom: 0.8` and renders at true size.
 *
 * 720, down from the freeze drawer's 820: at 820 nothing in here is wide enough
 * to use the space, so every label/value row grew a canyon between its two ends
 * and the pack card floated in a half-empty box. The five named photo slots are
 * the binding constraint at the bottom — three across at 720 leaves each 216px,
 * enough for "Shipment on weighing scale"; below that they clip, which is why
 * the `sm` step drops the grid to two across instead of narrowing further.
 *
 * The `<Drawer>` stays mounted so its exit transition plays; the body unmounts
 * on close and is KEYED ON THE SKU, so a fresh SKU is a fresh mount. That is the
 * freeze drawer's own contract — a fresh selection is always a fresh mount, so
 * there is deliberately no reset-on-reopen effect — reached structurally rather
 * than by re-seeding five setters by hand, which is how the old inline drawer
 * avoided the second SKU inheriting the first one's typed weights.
 */
export default function SkuFreezeDrawer({open, sku, rows, onClose, onRequestFreeze, onImageClick, fetchSkuHistory}) {
  return (
    <Drawer
      anchor='right'
      open={open}
      onClose={onClose}
      sx={{'& .MuiDrawer-paper': {width: {xs: '100%', sm: 660, md: 720}, maxWidth: '100%'}}}
    >
      {sku && (
        <FreezeBody
          key={skuKey(sku)}
          sku={sku}
          rows={rows}
          onClose={onClose}
          onRequestFreeze={onRequestFreeze}
          onImageClick={onImageClick}
          fetchSkuHistory={fetchSkuHistory}
        />
      )}
    </Drawer>
  )
}
